import React, { Suspense, lazy, useEffect, createContext, useContext, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SettingsProvider } from './context/SettingsContext';
import { WebSocketProvider } from './context/WebSocketContext';
import LoadingSpinner from './components/common/LoadingSpinner';
import AIAura from './components/common/AIAura';
import PermissionGate from './components/admin/PermissionGate';
import api from './services/api';
import toast from './utils/toast';
import ServiceUnavailablePopup from './components/common/ServiceUnavailablePopup';
import AppUpdatePrompt from './components/common/AppUpdatePrompt';
import ScratchCardPopup from './components/common/ScratchCardPopup';
import usePlatform from './hooks/usePlatform';

export const ThemeContext = createContext({ theme: 'dark', toggleTheme: () => {}, userTheme: 'default', setUserTheme: () => {} });
export function useTheme() { return useContext(ThemeContext); }

const ADMIN_ROLES = ['admin', 'super_admin', 'support_admin', 'team_admin'];

function SuspendedRedirect({ destination = '/dashboard', pathName = '' }) {
  const navigate = useNavigate();

  useEffect(() => {
    const blockedLabel = pathName.startsWith('/withdraw')
      ? 'withdrawals'
      : pathName.startsWith('/tasks')
      ? 'tasks and events'
      : 'this section';
    toast.error(`Your account is temporarily suspended from ${blockedLabel}. Please contact support for help.`);
    navigate(destination, { replace: true });
  }, [destination, navigate, pathName]);

  return <LoadingSpinner />;
}

function NotFoundPage() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  return (
    <div style={{ minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',flexDirection:'column',gap:16,background:isDark?'linear-gradient(160deg,#070714,#030711)':'#f8fafc' }}>
      <div style={{ fontSize:72 }}>🔍</div>
      <h1 style={{ color:isDark?'#fff':'#1e293b',fontSize:28,fontWeight:700 }}>404 - Page Not Found</h1>
      <p style={{ color:isDark?'#6b7280':'#64748b',fontSize:14 }}>The page you're looking for doesn't exist.</p>
      <button onClick={() => navigate(-1)} style={{ background:'#6366f1',color:'#fff',padding:'12px 28px',borderRadius:12,border:'none',cursor:'pointer',fontWeight:600,fontSize:14 }}>Go Back</button>
    </div>
  );
}

// ── User pages ────────────────────────────────────────────────────────────────
const LoginPage           = lazy(() => import('./pages/user/LoginPage'));
const SignupPage          = lazy(() => import('./pages/user/SignupPage'));
const Dashboard           = lazy(() => import('./pages/user/Dashboard'));
const InvestmentsPage     = lazy(() => import('./pages/user/InvestmentsPage'));
const RechargePage        = lazy(() => import('./pages/user/RechargePage'));
const WithdrawalPage      = lazy(() => import('./pages/user/WithdrawalPage'));
const ReferralsPage       = lazy(() => import('./pages/user/ReferralsPage'));
const TasksPage           = lazy(() => import('./pages/user/TasksPage'));
const ProfilePage         = lazy(() => import('./pages/user/ProfilePage'));
const SupportPage         = lazy(() => import('./pages/user/SupportPage'));
const NotificationsPage   = lazy(() => import('./pages/user/NotificationsPage'));
const VIPPage             = lazy(() => import('./pages/user/VIPPage'));
const WalletHistoryPage   = lazy(() => import('./pages/user/WalletHistoryPage'));
const PromotionCenterPage = lazy(() => import('./pages/user/PromotionCenterPage'));
const LeaderboardPage     = lazy(() => import('./pages/user/LeaderboardPage'));
const SpinWheelPage       = lazy(() => import('./pages/user/SpinWheelPage'));
const ScratchCardPage     = lazy(() => import('./pages/user/ScratchCardPage'));
const BonusCodePage       = lazy(() => import('./pages/user/BonusCodePage'));
const StreakPage           = lazy(() => import('./pages/user/StreakPage'));
const WalletTransferPage  = lazy(() => import('./pages/user/WalletTransferPage'));
const ForgotPasswordPage  = lazy(() => import('./pages/user/ForgotPasswordPage'));
const ResetPasswordPage   = lazy(() => import('./pages/user/ResetPasswordPage'));
const MaintenancePage     = lazy(() => import('./pages/MaintenancePage'));
const LandingPage         = lazy(() => import('./pages/public/LandingPage'));
const PrivacyPolicyPage   = lazy(() => import('./pages/public/PrivacyPolicyPageV2'));
const TermsOfServicePage  = lazy(() => import('./pages/public/TermsOfServicePageV2'));

// ── Admin pages ───────────────────────────────────────────────────────────────
const AdminDashboard          = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminUsers              = lazy(() => import('./pages/admin/AdminUsers'));
const AdminWithdrawals        = lazy(() => import('./pages/admin/AdminWithdrawals'));
const AdminRecharge           = lazy(() => import('./pages/admin/AdminRecharge'));
const AdminInvestments        = lazy(() => import('./pages/admin/AdminInvestments'));
const AdminVIP                = lazy(() => import('./pages/admin/AdminVIP'));
const AdminSettings           = lazy(() => import('./pages/admin/AdminSettings'));
const AdminLegalDocuments     = lazy(() => import('./pages/admin/AdminLegalDocuments'));
const AdminAuditLogs          = lazy(() => import('./pages/admin/AdminAuditLogs'));
const AdminTasks              = lazy(() => import('./pages/admin/AdminTasks'));
const AdminBonusCodes         = lazy(() => import('./pages/admin/AdminBonusCodes'));
const AdminPromotions         = lazy(() => import('./pages/admin/AdminPromotions'));
const AdminReferrals          = lazy(() => import('./pages/admin/AdminReferrals'));
const AdminSupport            = lazy(() => import('./pages/admin/AdminSupport'));
const AdminRefunds            = lazy(() => import('./pages/admin/AdminRefunds'));
const AdminLeaderboard        = lazy(() => import('./pages/admin/AdminLeaderboard'));
const AdminLeaderboardRewards = lazy(() => import('./pages/admin/AdminLeaderboardRewards'));
const Adminbanners            = lazy(() => import('./pages/admin/Adminbanners'));
const AdminSystemMonitoring   = lazy(() => import('./pages/admin/AdminSystemMonitoring'));
const AdminAnalytics          = lazy(() => import('./pages/admin/AdminAnalytics'));
const AdminSpinWheel          = lazy(() => import('./pages/admin/AdminSpinWheel'));
const AdminScratchCard        = lazy(() => import('./pages/admin/AdminScratchCard'));
const AdminStreakControl      = lazy(() => import('./pages/admin/AdminStreakControl'));
const AdminPushNotifications  = lazy(() => import('./pages/admin/AdminPushNotifications'));
const AdminRoles              = lazy(() => import('./pages/admin/AdminRoles'));
const AdminFraudAlerts        = lazy(() => import('./pages/admin/AdminFraudAlerts'));
const AdminHelpCenter         = lazy(() => import('./pages/admin/AdminHelpCenter'));
const HelpCenterPage          = lazy(() => import('./pages/user/HelpCenterPage'));
const BugReportPage           = lazy(() => import('./pages/user/BugReportPage'));
const UserReviewsPage         = lazy(() => import('./pages/user/UserReviewsPage'));
const AdminAppearance         = lazy(() => import('./pages/admin/AdminAppearance'));
const AdminBugReports         = lazy(() => import('./pages/admin/AdminBugReports'));
const AdminUserReviews        = lazy(() => import('./pages/admin/AdminUserReviews'));

// ── Guards ────────────────────────────────────────────────────────────────────
function MaintenanceGuard({ children }) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = ADMIN_ROLES.includes(user?.role);
  useEffect(() => {
    if (loading || isAdmin) return;
    // Admin paths ALWAYS bypass maintenance — admin must always access panel
    if (location.pathname.startsWith('/admin')) return;
    // Only maintenance and reset pages are always safe to show
    const alwaysAllowed = ['/maintenance', '/reset-password', '/privacy-policy', '/terms-of-service'];
    if (alwaysAllowed.some(p => location.pathname.startsWith(p))) return;
    // /login, /signup, /forgot-password — non-admin users still redirect to maintenance
    // Small debounce — prevents race condition right after login navigate()
    const t = setTimeout(() => {
      api.get('/system/settings/public').then(r => {
        const val = r.data?.maintenance_mode;
        const isMaint = val === true || val === 'true' || (typeof val === 'string' && val.replace(/"/g,'').trim() === 'true');
        if (isMaint) navigate('/maintenance', { replace: true });
      }).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [location.pathname, isAdmin, loading, navigate]);
  return children;
}

function PrivateRoute({ children, adminOnly = false }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <LoadingSpinner />;
  if (!user) {
    const next = encodeURIComponent(`${location.pathname}${location.search}${location.hash}`);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  if (adminOnly && !ADMIN_ROLES.includes(user.role)) return <Navigate to="/dashboard" replace />;
  const suspendedBlockedPaths = ['/withdraw', '/tasks', '/spin', '/streak', '/scratch-card', '/bonus-codes'];
  if (
    user.role === 'user' &&
    user.status === 'suspended' &&
    suspendedBlockedPaths.some(p => location.pathname === p || location.pathname.startsWith(`${p}/`))
  ) {
    return <SuspendedRedirect destination="/dashboard" pathName={location.pathname} />;
  }
  return children;
}

// AdminRoute: checks auth + wraps with PermissionGate for section enforcement
function AdminRoute({ path, children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (!ADMIN_ROLES.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return <PermissionGate path={path}>{children}</PermissionGate>;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <LoadingSpinner />;
  // reset-password must always be accessible — even if logged in (admin or user)
  const isResetPath = location.pathname === '/reset-password' || location.pathname.startsWith('/reset-password');
  if (user && !isResetPath) {
    const next = new URLSearchParams(location.search).get('next');
    const fallback = ADMIN_ROLES.includes(user.role) ? '/admin' : '/dashboard';
    return <Navigate to={next || fallback} replace />;
  }
  return children;
}

function AIAuraConditional() {
  const location = useLocation();
  // Only show on Dashboard
  const allowed = ['/dashboard'];
  const show = allowed.some(p => location.pathname === p || location.pathname.startsWith(p + '/'));
  if (!show) return null;
  return <AIAura />;
}

function ThemeRouteSync({ theme, userTheme }) {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname;
    const isUserForcedPage = ['/login', '/signup', '/reset-password', '/forgot-password', '/maintenance'].some(p => path.startsWith(p));
    const isActualAdminRoute = path.startsWith('/admin') && !isUserForcedPage;

    document.documentElement.removeAttribute('data-admin-theme');

    if (isActualAdminRoute) {
      document.documentElement.removeAttribute('data-user-theme');
      document.documentElement.removeAttribute('data-theme');
      return;
    }

    if (userTheme && userTheme !== 'default' && userTheme !== 'light') {
      document.documentElement.setAttribute('data-user-theme', userTheme);
      document.documentElement.removeAttribute('data-theme');
      return;
    }

    document.documentElement.removeAttribute('data-user-theme');
    document.documentElement.setAttribute('data-theme', theme === 'dark' ? 'dark' : 'light');
  }, [location.pathname, theme, userTheme]);

  useEffect(() => {
    if (typeof window.__hideSplash === 'function') window.__hideSplash();
  }, []);

  return null;
}

function AppRoutes() {
  const { user } = useAuth();

  return (
    <MaintenanceGuard>
      <ErrorBoundary>
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          <Route path="/" element={<LandingPage />} />

          {/* Public */}
          <Route path="/login"           element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/signup"          element={<PublicRoute><SignupPage /></PublicRoute>} />
          <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
          <Route path="/reset-password"  element={<ResetPasswordPage />} />  {/* No auth guard - always accessible */}
          <Route path="/maintenance"     element={<MaintenancePage />} />
          <Route path="/privacy-policy"   element={<PrivacyPolicyPage />} />
          <Route path="/terms-of-service" element={<TermsOfServicePage />} />

          {/* User */}
          <Route path="/dashboard"      element={<PrivateRoute><Dashboard /></PrivateRoute>} />
          <Route path="/investments"    element={<PrivateRoute><InvestmentsPage /></PrivateRoute>} />
          <Route path="/recharge"       element={<PrivateRoute><RechargePage /></PrivateRoute>} />
          <Route path="/withdraw"       element={<PrivateRoute><WithdrawalPage /></PrivateRoute>} />
          <Route path="/referrals"      element={<PrivateRoute><ReferralsPage /></PrivateRoute>} />
          <Route path="/tasks"          element={<PrivateRoute><TasksPage /></PrivateRoute>} />
          <Route path="/profile"        element={<PrivateRoute><ProfilePage /></PrivateRoute>} />
          <Route path="/support"        element={<PrivateRoute><SupportPage /></PrivateRoute>} />
          <Route path="/notifications"  element={<PrivateRoute><NotificationsPage /></PrivateRoute>} />
          <Route path="/vip"            element={<PrivateRoute><VIPPage /></PrivateRoute>} />
          <Route path="/promotions"     element={<PrivateRoute><PromotionCenterPage /></PrivateRoute>} />
          <Route path="/wallet-history" element={<PrivateRoute><WalletHistoryPage /></PrivateRoute>} />
          <Route path="/leaderboard"    element={<PrivateRoute><LeaderboardPage /></PrivateRoute>} />
          <Route path="/spin"           element={<PrivateRoute><SpinWheelPage /></PrivateRoute>} />
          <Route path="/scratch-card"    element={<PrivateRoute><ScratchCardPage /></PrivateRoute>} />
          <Route path="/bonus-codes"      element={<PrivateRoute><BonusCodePage /></PrivateRoute>} />
          <Route path="/streak"         element={<PrivateRoute><StreakPage /></PrivateRoute>} />
          <Route path="/transfer"       element={<PrivateRoute><WalletTransferPage /></PrivateRoute>} />
          <Route path="/help"            element={<PrivateRoute><HelpCenterPage /></PrivateRoute>} />
          <Route path="/bug-report"      element={<PrivateRoute><BugReportPage /></PrivateRoute>} />
          <Route path="/reviews"         element={<PrivateRoute><UserReviewsPage /></PrivateRoute>} />

          {/* Admin - each wrapped with PermissionGate via AdminRoute */}
          <Route path="/admin"                     element={<AdminRoute path="/admin"><AdminDashboard /></AdminRoute>} />
          <Route path="/admin/users"               element={<AdminRoute path="/admin/users"><AdminUsers /></AdminRoute>} />
          <Route path="/admin/withdrawals"         element={<AdminRoute path="/admin/withdrawals"><AdminWithdrawals /></AdminRoute>} />
          <Route path="/admin/recharge"            element={<AdminRoute path="/admin/recharge"><AdminRecharge /></AdminRoute>} />
          <Route path="/admin/investments"         element={<AdminRoute path="/admin/investments"><AdminInvestments /></AdminRoute>} />
          <Route path="/admin/vip"                 element={<AdminRoute path="/admin/vip"><AdminVIP /></AdminRoute>} />
          <Route path="/admin/settings"            element={<AdminRoute path="/admin/settings"><AdminSettings /></AdminRoute>} />
          <Route path="/admin/terms"               element={<AdminRoute path="/admin/terms"><AdminLegalDocuments /></AdminRoute>} />
          <Route path="/admin/audit-logs"          element={<AdminRoute path="/admin/audit-logs"><AdminAuditLogs /></AdminRoute>} />
          <Route path="/admin/monitoring" element={<AdminRoute path="/admin/monitoring"><AdminSystemMonitoring /></AdminRoute>} />
          <Route path="/admin/tasks"               element={<AdminRoute path="/admin/tasks"><AdminTasks /></AdminRoute>} />
          <Route path="/admin/bonus-codes"         element={<AdminRoute path="/admin/bonus-codes"><AdminBonusCodes /></AdminRoute>} />
          <Route path="/admin/promotions"          element={<AdminRoute path="/admin/promotions"><AdminPromotions /></AdminRoute>} />
          <Route path="/admin/referrals"           element={<AdminRoute path="/admin/referrals"><AdminReferrals /></AdminRoute>} />
          <Route path="/admin/support"             element={<AdminRoute path="/admin/support"><AdminSupport /></AdminRoute>} />
          <Route path="/admin/refunds"             element={<AdminRoute path="/admin/refunds"><AdminRefunds /></AdminRoute>} />
          <Route path="/admin/leaderboard"         element={<AdminRoute path="/admin/leaderboard"><AdminLeaderboard /></AdminRoute>} />
          <Route path="/admin/leaderboard-rewards" element={<AdminRoute path="/admin/leaderboard-rewards"><AdminLeaderboardRewards /></AdminRoute>} />
          <Route path="/admin/banners"             element={<AdminRoute path="/admin/banners"><Adminbanners /></AdminRoute>} />
          <Route path="/admin/spin-wheel"          element={<AdminRoute path="/admin/spin-wheel"><AdminSpinWheel /></AdminRoute>} />
          <Route path="/admin/scratch-card"         element={<AdminRoute path="/admin/scratch-card"><AdminScratchCard /></AdminRoute>} />
          <Route path="/admin/streak"              element={<AdminRoute path="/admin/streak"><AdminStreakControl /></AdminRoute>} />
          <Route path="/admin/push-notifications"  element={<AdminRoute path="/admin/push-notifications"><AdminPushNotifications /></AdminRoute>} />
          <Route path="/admin/roles"               element={<AdminRoute path="/admin/roles"><AdminRoles /></AdminRoute>} />
          <Route path="/admin/appearance"          element={<AdminRoute path="/admin/appearance"><AdminAppearance /></AdminRoute>} />
          <Route path="/admin/analytics"          element={<AdminRoute path="/admin/analytics"><AdminAnalytics /></AdminRoute>} />
          <Route path="/admin/fraud-alerts"         element={<AdminRoute path="/admin/fraud-alerts"><AdminFraudAlerts /></AdminRoute>} />
          <Route path="/admin/help-center"          element={<AdminRoute path="/admin/help-center"><AdminHelpCenter /></AdminRoute>} />
          <Route path="/admin/bug-reports"          element={<AdminRoute path="/admin/bug-reports"><AdminBugReports /></AdminRoute>} />
          <Route path="/admin/user-reviews"         element={<AdminRoute path="/admin/user-reviews"><AdminUserReviews /></AdminRoute>} />

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
      </ErrorBoundary>
      {user && <AIAuraConditional />}
      {user && !ADMIN_ROLES.includes(user?.role) && <ScratchCardPopup />}
    </MaintenanceGuard>
  );
}

// Theme-aware toast — reads CSS vars so it follows user theme automatically
function ToastWrapper() {
  // useTheme() ensures re-render when user switches dark/light
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  // Theme-specific values — no CSS var lookup needed, use direct values
  const colors = isDark ? {
    bg:          '#1a1d2e',
    border:      'rgba(99,102,241,0.25)',
    text:        '#f1f5f9',
    successBg:   '#0a1f16',
    errorBg:     '#1f0a0a',
    loadingBg:   '#0f1623',
    borderSucc:  'rgba(16,185,129,0.35)',
    borderDang:  'rgba(239,68,68,0.35)',
    borderGlow:  'rgba(99,102,241,0.3)',
    shadow:      '0 12px 40px rgba(0,0,0,0.5)',
  } : {
    bg:          '#ffffff',
    border:      'rgba(99,102,241,0.20)',
    text:        '#1e1b4b',      // dark text on light bg
    successBg:   '#f0fdf4',
    errorBg:     '#fff1f2',
    loadingBg:   '#f5f3ff',
    borderSucc:  'rgba(5,150,105,0.30)',
    borderDang:  'rgba(225,29,72,0.30)',
    borderGlow:  'rgba(99,102,241,0.20)',
    shadow:      '0 8px 32px rgba(99,102,241,0.15)',
  };

  const base = {
    background:          colors.bg,
    color:               colors.text,
    WebkitTextFillColor: colors.text,  // prevent CSS overrides from changing toast text color
    border:              `1px solid ${colors.border}`,
    borderRadius: '16px',
    fontSize:     '13px',
    fontWeight:   '600',
    maxWidth:     '360px',
    fontFamily:   'Outfit, sans-serif',
    padding:      '12px 16px',
    boxShadow:    colors.shadow,
  };

  const rose    = '#ef4444';
  const accent  = '#6366f1';

  return (
    <Toaster
      key={theme}
      position="top-center"
      containerStyle={{ top: 16 }}
      containerClassName="toast-safe"
      gutter={8}
      toastOptions={{
        duration: 2800,
        style: base,
        success: {
          duration: 2500,
          iconTheme: { primary: '#ffffff', secondary: '#065f46' },
          style: {
            ...base,
            background: '#065f46',
            border: '1px solid rgba(16,185,129,0.38)',
            color: '#ffffff',
            WebkitTextFillColor: '#ffffff',
            fontWeight: '700',
          },
        },
        error: {
          duration: 3500,
          iconTheme: { primary: rose, secondary: colors.errorBg },
          style: { ...base, background: colors.errorBg, border: `1px solid ${colors.borderDang}`, color: colors.text },
        },
        loading: {
          iconTheme: { primary: accent, secondary: colors.loadingBg },
          style: { ...base, background: colors.loadingBg, border: `1px solid ${colors.borderGlow}`, color: colors.text },
        },
        blank: {
          iconTheme: { primary: accent, secondary: colors.bg },
          style: { ...base, color: colors.text },
        },
      }}
    />
  );
}

// ErrorBoundary prevents one crashed component from killing whole app
class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error('[ErrorBoundary]', error.message, info.componentStack?.split('\n')[1]); }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 32, textAlign: 'center', color: '#9ca3af' }}>
          <p style={{ fontSize: 32, marginBottom: 12 }}>⚠️</p>
          <p style={{ fontWeight: 700, marginBottom: 8, color: '#f1f5f9' }}>Something went wrong</p>
          <p style={{ fontSize: 13, marginBottom: 16 }}>{this.state.error?.message}</p>
          <button onClick={() => { this.setState({ hasError: false }); window.location.reload(); }}
            style={{ padding: '8px 20px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 700 }}>
            Reload Page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function App() {
  usePlatform(); // applies platform name/logo/favicon from settings globally
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'light');
  const [userTheme, setUserThemeState] = useState(() => localStorage.getItem('user_theme') || 'light');

  function toggleTheme() {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    localStorage.setItem('theme', next);
    document.documentElement.setAttribute('data-theme', next);
  }

  function setUserTheme(t) {
    setUserThemeState(t);
    localStorage.setItem('user_theme', t);
    if (t === 'default' || t === 'light') {
      document.documentElement.removeAttribute('data-user-theme');
    } else {
      document.documentElement.setAttribute('data-user-theme', t);
    }
  }

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'user_theme') {
        setUserThemeState(e.newValue || 'default');
      }
      if (e.key === 'theme') {
        setTheme(e.newValue || 'light');
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);
  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, userTheme, setUserTheme }}>
      <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || ''}>
        <BrowserRouter>
          <ThemeRouteSync theme={theme} userTheme={userTheme} />
          <AuthProvider>
            <SettingsProvider>
              <WebSocketProvider>
                <AppRoutes />
                <ToastWrapper />
              </WebSocketProvider>
            </SettingsProvider>
          </AuthProvider>
                <ServiceUnavailablePopup />
                <AppUpdatePrompt />
      </BrowserRouter>
      </GoogleOAuthProvider>
    </ThemeContext.Provider>
  );
}
